import React, { useState, useEffect } from 'react';
import { Clock, Package, ChefHat, CheckCircle, Truck, Home, Star, MessageSquare, X, ThumbsUp, ThumbsDown, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { toast } from 'sonner';

const OrderTracking = ({ order, onClose, onRateProduct, onRaiseTicket }) => {
  const [eta, setEta] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  
  // Rating states
  const [orderRating, setOrderRating] = useState(order.order_rating || 0);
  const [orderReview, setOrderReview] = useState(order.order_review || '');
  const [productRatings, setProductRatings] = useState({});
  const [packagingFeedback, setPackagingFeedback] = useState(null);
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);

  const isDelivered = order.status === 'delivered' || order.status === 'completed';

  const statusSteps = [
    { key: 'placed', label: 'Order Placed', icon: Package, message: 'Order received 👍' },
    { key: 'accepted', label: 'Accepted', icon: CheckCircle, message: 'Kitchen confirmed 👨‍🍳' },
    { key: 'preparing', label: 'Preparing', icon: ChefHat, message: 'Cooking your order 🔥' },
    { key: 'ready', label: 'Ready', icon: Package, message: 'Packed & ready 🎁' },
    { key: 'out_for_delivery', label: 'On the Way', icon: Truck, message: 'On the way 🚚' },
    { key: 'delivered', label: 'Delivered', icon: Home, message: 'Delivered ✅' },
  ];

  // Filter steps for pickup orders
  const displaySteps = order.order_type === 'pickup' 
    ? statusSteps.filter(s => !['out_for_delivery'].includes(s.key))
    : statusSteps;

  const currentStepIndex = displaySteps.findIndex(s => s.key === order.status);
  const progressPercent = eta?.progress_percent || ((currentStepIndex + 1) / displaySteps.length) * 100;

  useEffect(() => {
    const fetchEta = async () => {
      try {
        const response = await api.get(`/orders/${order.id}/eta`);
        setEta(response.data);
        setRemainingTime(response.data.remaining_minutes);
      } catch (error) {
        console.error('Error fetching ETA:', error);
      }
    };

    fetchEta();
    const interval = setInterval(fetchEta, 30000);
    return () => clearInterval(interval);
  }, [order.id]);

  // Countdown timer
  useEffect(() => {
    if (remainingTime <= 0 || isDelivered) return;

    const timer = setInterval(() => {
      setRemainingTime(prev => Math.max(0, prev - 1));
    }, 60000);

    return () => clearInterval(timer);
  }, [remainingTime, isDelivered]);

  // Handle overall order rating
  const handleOrderRating = async (rating) => {
    setOrderRating(rating);
    try {
      await api.post('/orders/rate', {
        order_id: order.id,
        rating: rating,
        review: orderReview
      }, {
        headers: { 'x-customer-phone': order.customer_phone }
      });
      toast.success('Thanks for rating your order!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rating');
    }
  };

  // Handle order review submission
  const handleSubmitReview = async () => {
    if (!orderReview.trim()) return;
    
    setIsSubmittingRating(true);
    try {
      await api.post('/orders/rate', {
        order_id: order.id,
        rating: orderRating || 5,
        review: orderReview
      }, {
        headers: { 'x-customer-phone': order.customer_phone }
      });
      toast.success('Review submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setIsSubmittingRating(false);
    }
  };

  // Handle product rating (inline)
  const handleProductRating = async (productId, rating) => {
    setProductRatings(prev => ({ ...prev, [productId]: rating }));
    
    try {
      await api.post(`/products/${productId}/rate`, {
        product_id: productId,
        order_id: order.id,
        rating: rating
      }, {
        headers: { 'x-customer-phone': order.customer_phone }
      });
      toast.success('Product rated!');
    } catch (error) {
      // Silently handle if already rated
      if (!error.response?.data?.detail?.includes('already rated')) {
        toast.error(error.response?.data?.detail || 'Failed to rate product');
      }
    }
  };

  const handleRaiseTicket = async () => {
    if (!ticketSubject || !ticketDescription) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await api.post('/tickets', {
        order_id: order.id,
        subject: ticketSubject,
        description: ticketDescription
      }, {
        headers: { 'x-customer-phone': order.customer_phone }
      });
      toast.success('Support ticket created successfully');
      setShowTicketModal(false);
      setTicketSubject('');
      setTicketDescription('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    }
  };

  // Handle invoice download
  const handleDownloadInvoice = async () => {
    try {
      const response = await api.get(`/orders/${order.id}/invoice`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${order.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded!');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const formatEtaRange = () => {
    if (!eta || isDelivered) return null;
    
    if (order.status === 'ready' && order.order_type === 'pickup') {
      return 'Ready for pickup!';
    }
    
    if (order.status === 'out_for_delivery') {
      return `Arriving in ${eta.eta_range} mins`;
    }
    
    if (order.status === 'preparing') {
      return `Ready in ~${Math.max(0, eta.prep_time - 5)}-${eta.prep_time + 5} mins`;
    }
    
    return `${eta.eta_range} mins`;
  };

  // Star rating component
  const StarRating = ({ rating, onRate, size = 'md', disabled = false }) => {
    const sizeClass = size === 'sm' ? 'w-5 h-5' : size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => !disabled && onRate(star)}
            disabled={disabled}
            className={`focus:outline-none transition-transform ${!disabled && 'hover:scale-110'}`}
          >
            <Star
              className={`${sizeClass} ${
                star <= rating 
                  ? 'fill-yellow-400 text-yellow-400' 
                  : 'text-gray-500'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-[#1a1a1a] rounded-xl shadow-lg p-6 relative">
      {/* Close Button */}
      {onClose && (
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      )}
      
      {/* Order Header */}
      <div className="flex justify-between items-start mb-4 pr-10">
        <div>
          <h3 className="text-lg font-bold text-white">{order.order_number}</h3>
          <p className="text-sm text-gray-400">
            {order.order_type === 'delivery' ? 'Delivery' : 'Pickup'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-orange-500">
            {eta?.status_message || order.status}
          </p>
          {formatEtaRange() && (
            <p className="text-sm text-gray-400 flex items-center justify-end gap-1 mt-1">
              <Clock className="w-4 h-4" />
              {formatEtaRange()}
            </p>
          )}
        </div>
      </div>

      {/* Delay Banner - Only show if order is delayed by more than 15 mins */}
      {!isDelivered && (() => {
        const prepTime = order.prep_time_estimate || 20;
        const deliveryTime = order.order_type === 'delivery' ? (order.delivery_time_estimate || 15) : 0;
        const bufferTime = order.buffer_time || 5;
        
        // Check if preparation has started
        const preparationStarted = ['preparing', 'ready', 'out_for_delivery'].includes(order.status);
        if (!preparationStarted) return null; // No delay possible before prep starts
        
        const statusUpdated = order.status_updated_at ? new Date(order.status_updated_at) : new Date(order.created_at);
        const expectedDelivery = new Date(statusUpdated.getTime() + (prepTime + deliveryTime + bufferTime) * 60000);
        const now = new Date();
        const isDelayed = now > expectedDelivery;
        const delayMins = isDelayed ? Math.round((now - expectedDelivery) / 60000) : 0;
        
        // Only show delay message if delayed by more than 15 mins
        if (delayMins <= 15) return null;
        
        return (
          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 mb-4 text-center">
            <p className="text-yellow-400 font-medium text-sm">
              ⏰ Your order is taking a bit longer than usual. Thanks for your patience! 🙏
            </p>
          </div>
        );
      })()}

      {/* Progress Bar - Only show if not delivered */}
      {!isDelivered && (
        <div className="mb-6">
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          {(() => {
            // Calculate estimated time - use ETA API data, or fallback to order estimates
            const prepTime = order.prep_time_estimate || 20;
            const deliveryTime = order.order_type === 'delivery' ? (order.delivery_time_estimate || 15) : 0;
            const bufferTime = order.buffer_time || 5;
            const totalEstimate = prepTime + deliveryTime + bufferTime;
            
            // Check if preparation has started
            const preparationStarted = ['preparing', 'ready', 'out_for_delivery'].includes(order.status);
            
            let displayTime;
            
            if (!preparationStarted) {
              // Before preparation - show fixed estimated time (no countdown)
              displayTime = totalEstimate;
            } else {
              // After preparation started - calculate remaining time
              const statusUpdated = order.status_updated_at ? new Date(order.status_updated_at) : new Date(order.created_at);
              const expectedDelivery = new Date(statusUpdated.getTime() + (prepTime + deliveryTime + bufferTime) * 60000);
              const now = new Date();
              
              displayTime = Math.max(0, Math.round((expectedDelivery - now) / 60000));
              
              // Check if delayed
              const isDelayed = now > expectedDelivery;
              const delayMins = isDelayed ? Math.round((now - expectedDelivery) / 60000) : 0;
              
              // If delayed, add extra buffer time
              if (isDelayed) {
                if (delayMins <= 10) {
                  displayTime = 5 + Math.ceil(delayMins / 2);
                } else if (delayMins <= 20) {
                  displayTime = 10 + Math.ceil(delayMins / 3);
                } else {
                  displayTime = 15 + Math.ceil(delayMins / 4);
                }
              }
            }
            
            // Round to nearest 5 mins
            displayTime = Math.ceil(displayTime / 5) * 5;
            if (displayTime < 5) displayTime = 5;
            
            return (
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-gray-400">
                  {!preparationStarted ? 'Estimated' : '~'}{displayTime} mins {preparationStarted ? 'remaining' : ''}
                </p>
                <p className="text-xs text-orange-500 font-medium">
                  {!preparationStarted 
                    ? 'Waiting for kitchen' 
                    : (order.order_type === 'delivery' ? 'Delivery time' : 'Pickup time')}
                </p>
              </div>
            );
          })()}
        </div>
      )}

      {/* Status Steps - Compact for delivered orders */}
      {!isDelivered && (
        <div className="flex justify-between mb-6">
          {displaySteps.map((step, index) => {
            const StepIcon = step.icon;
            const isCompleted = index <= currentStepIndex;
            const isCurrent = index === currentStepIndex;

            return (
              <div key={step.key} className="flex flex-col items-center flex-1">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all
                  ${isCompleted 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-white/10 text-gray-500'}
                  ${isCurrent ? 'ring-4 ring-orange-500/30 scale-110' : ''}
                `}>
                  <StepIcon className="w-5 h-5" />
                </div>
                <span className={`text-xs text-center ${isCompleted ? 'text-white font-medium' : 'text-gray-500'}`}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ============ RATING SECTION (Only for delivered orders) ============ */}
      {isDelivered && (
        <div className="bg-[#252525] rounded-xl p-4 mb-6 space-y-4">
          {/* Overall Order Rating */}
          <div className="text-center pb-4 border-b border-white/10">
            <p className="text-white font-medium mb-2">Rate this Order</p>
            <StarRating 
              rating={orderRating} 
              onRate={handleOrderRating} 
              size="lg"
            />
          </div>

          {/* Review Textarea */}
          <div>
            <Textarea
              placeholder="Write a review (optional)..."
              value={orderReview}
              onChange={(e) => setOrderReview(e.target.value)}
              className="bg-[#1a1a1a] border-white/10 text-white placeholder:text-gray-500 min-h-[80px]"
            />
            {orderReview.trim() && (
              <Button 
                onClick={handleSubmitReview}
                disabled={isSubmittingRating}
                className="w-full mt-2 bg-orange-500 hover:bg-orange-600 text-white"
                size="sm"
              >
                {isSubmittingRating ? 'Submitting...' : 'Submit Review'}
              </Button>
            )}
          </div>

          {/* Packaging Feedback */}
          <div className="pt-4 border-t border-white/10">
            <p className="text-gray-400 text-sm mb-2">How was the packaging?</p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPackagingFeedback('good')}
                className={`flex-1 ${packagingFeedback === 'good' ? 'bg-green-500/20 border-green-500 text-green-400' : 'border-white/20 text-white'}`}
              >
                <ThumbsUp className="w-4 h-4 mr-1" />
                Good
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPackagingFeedback('not_good')}
                className={`flex-1 ${packagingFeedback === 'not_good' ? 'bg-red-500/20 border-red-500 text-red-400' : 'border-white/20 text-white'}`}
              >
                <ThumbsDown className="w-4 h-4 mr-1" />
                Not Good
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Order Items with inline ratings */}
      <div className="border-t border-white/10 pt-4">
        <h4 className="font-semibold text-white mb-3">Order Items</h4>
        {order.items?.map((item, index) => {
          const itemRating = productRatings[item.product_id] || 0;
          
          return (
            <div key={index} className="py-3 border-b border-white/10 last:border-0">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <p className="text-white">{item.product_name}</p>
                  <p className="text-sm text-gray-400">Qty: {item.quantity} × ₹{item.price}</p>
                </div>
                <span className="text-white font-medium">₹{item.price * item.quantity}</span>
              </div>
              
              {/* Inline Product Rating - Only for delivered orders */}
              {isDelivered && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-gray-500 text-xs">Rate:</span>
                  <StarRating 
                    rating={itemRating} 
                    onRate={(r) => handleProductRating(item.product_id, r)} 
                    size="sm"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Order Total */}
      <div className="border-t border-white/10 mt-4 pt-4">
        <div className="flex justify-between text-white font-bold text-lg">
          <span>Total</span>
          <span className="text-[#FF4500]">₹{order.total_amount?.toFixed(2)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 mt-6">
        {/* Download Invoice Button - Only for completed orders */}
        {isDelivered && (
          <Button
            variant="outline"
            className="flex-1 border-green-500/50 text-green-400 hover:bg-green-500/10"
            onClick={handleDownloadInvoice}
          >
            <Download className="w-4 h-4 mr-2" />
            Invoice
          </Button>
        )}
        <Button
          variant="outline"
          className="flex-1 border-white/20 text-white hover:bg-white/10"
          onClick={() => setShowTicketModal(true)}
        >
          <MessageSquare className="w-4 h-4 mr-2" />
          Raise Issue
        </Button>
      </div>

      {/* Ticket Modal */}
      <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
        <DialogContent className="bg-[#1a1a1a] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Raise a Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-300 mb-1 block">Subject</label>
              <select
                value={ticketSubject}
                onChange={(e) => setTicketSubject(e.target.value)}
                className="w-full p-3 bg-[#252525] border border-white/10 rounded-lg text-white [&>option]:bg-[#252525] [&>option]:text-white"
              >
                <option value="" className="bg-[#252525] text-white">Select an issue</option>
                <option value="Wrong order" className="bg-[#252525] text-white">Wrong order received</option>
                <option value="Missing items" className="bg-[#252525] text-white">Missing items</option>
                <option value="Quality issue" className="bg-[#252525] text-white">Quality issue</option>
                <option value="Delivery issue" className="bg-[#252525] text-white">Delivery issue</option>
                <option value="Payment issue" className="bg-[#252525] text-white">Payment issue</option>
                <option value="Other" className="bg-[#252525] text-white">Other</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-300 mb-1 block">Description</label>
              <Textarea
                placeholder="Describe your issue..."
                value={ticketDescription}
                onChange={(e) => setTicketDescription(e.target.value)}
                className="min-h-[120px] bg-[#252525] border-white/10 text-white placeholder:text-gray-500"
              />
            </div>
            <Button 
              onClick={handleRaiseTicket}
              disabled={!ticketSubject || !ticketDescription}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
            >
              Submit Ticket
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderTracking;
